import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Banknote, ChevronRight, CircleAlert, CircleCheck, Plus, RefreshCw, Sparkles } from 'lucide-react';
import { Badge, EmptyState } from '../design/ui';
import RefundForm from './RefundForm';
import {
  GRANTED_STATUSES,
  formatRefundMoney,
  newRefundForm,
  refundAmountPending,
  refundPayload,
} from '../utils/refunds';
import './TicketRefunds.css';

// The ticket's « Rembours. » view (ticket window side panel): what was
// recorded on the ticket (count, granted amount, one row per refund) and the
// form to record a new one. The ticket context (order, client, declared
// values) comes from the refunds ticket lookup, so the form offers the same
// prefill as the refunds page (KI-158). The form fields are RefundForm's.

const REFUND_TONES = {
  requested: 'warning',
  approved: 'info',
  processing: 'info',
  paid: 'success',
  rejected: 'danger',
};
const TicketRefunds = ({ ticketId, ticket }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || 'fr';
  const navigate = useNavigate();
  const [refunds, setRefunds] = useState([]);
  const [context, setContext] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState(() => newRefundForm(null));

  const grantedTotal = useMemo(
    () => refunds
      .filter((refund) => GRANTED_STATUSES.includes(refund.status))
      .reduce((sum, refund) => sum + Number(refund.total_refund_amount || 0), 0),
    [refunds]
  );

  const token = localStorage.getItem('access_token');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchRefunds = useCallback(async () => {
    if (!ticketId) return;
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`/api/logistics/tickets/${ticketId}/refunds`, { headers });
      setRefunds(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Failed to fetch ticket refunds:', err);
      setError(t('refunds.fetchError', 'Failed to load refunds.'));
    } finally {
      setLoading(false);
    }
  }, [headers, t, ticketId]);

  useEffect(() => {
    fetchRefunds();
  }, [fetchRefunds]);

  useEffect(() => {
    if (!ticketId) return undefined;
    let cancelled = false;
    axios.get('/api/logistics/refunds/ticket-lookup', { headers, params: { q: ticketId } })
      .then((response) => {
        const item = response.data?.items?.[0] || null;
        if (cancelled) return;
        setContext(item);
        setForm(newRefundForm(item));
      })
      .catch((err) => console.error('Failed to load the ticket refund context:', err));
    return () => { cancelled = true; };
  }, [headers, ticketId]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await axios.post(`/api/logistics/tickets/${ticketId}/refunds`, refundPayload(form), { headers });
      setForm(newRefundForm(context));
      setSuccess(t('refunds.createSuccess', 'Refund created successfully.'));
      await fetchRefunds();
    } catch (err) {
      console.error('Failed to create refund:', err);
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : t('refunds.createError', 'Failed to create refund.'));
    } finally {
      setSaving(false);
    }
  };

  const orderReference = context?.order_reference;

  const contextParts = [
    ticket?.external_ticket_id,
    orderReference && t('refunds.orderShort', 'Cde {{order}}', { order: orderReference }),
    context?.client_name,
  ].filter(Boolean);

  return (
    <div className="tkr">
      <div className="tkr-head">
        <h3 className="tkr-head__title">{t('refunds.ticketTabTitle', 'Ticket refunds')}</h3>
        <button
          type="button"
          className="u-btn u-btn--ghost u-btn--icon u-btn--sm"
          onClick={fetchRefunds}
          disabled={loading}
          aria-label={t('refunds.refresh', 'Refresh')}
          title={t('refunds.refresh', 'Refresh')}
        >
          <RefreshCw strokeWidth={1.8} />
        </button>
      </div>

      {!loading && refunds.length > 0 && (
        <div className="tkr-facts">
          <div className="tkr-fact">
            <span className="u-overline">{t('refunds.totalCreated', 'Created')}</span>
            <strong className="u-num">{refunds.length}</strong>
            <span className="tkr-fact__context">{t('ticketWorkspaceUi.refunds.onTicket', 'sur ce ticket')}</span>
          </div>
          <div className="tkr-fact">
            <span className="u-overline">{t('refunds.grantedAmount', 'Granted')}</span>
            <strong className="u-num">{formatRefundMoney(grantedTotal, refunds[0]?.currency || form.currency, lang)}</strong>
            <span className="tkr-fact__context">{t('ticketWorkspaceUi.refunds.grantedScope', 'accordés, en cours ou payés')}</span>
          </div>
        </div>
      )}

      {contextParts.length > 0 && (
        <p className="tkr-context">{contextParts.join(' · ')}</p>
      )}

      {loading ? (
        <div className="tkr-skeleton" aria-busy="true">
          <span className="u-skeleton tkr-skeleton__row" />
          <span className="u-skeleton tkr-skeleton__row" />
        </div>
      ) : refunds.length === 0 ? (
        <EmptyState icon={Banknote} title={t('ticketWorkspaceUi.refunds.emptyTitle', 'Aucun remboursement')}>
          {t('refunds.emptyTicket', 'No refunds created for this ticket yet.')}
        </EmptyState>
      ) : (
        <ul className="tkr-list" aria-label={t('refunds.existingRefunds', 'Existing refunds')}>
          {refunds.map((refund) => (
            <li key={refund._id}>
              <button type="button" className="tkr-row" onClick={() => navigate(`/refunds/${refund._id}`)}>
                <span className="tkr-row__main">
                  <strong className="tkr-row__amount u-num">
                    {refundAmountPending(refund)
                      ? t('refunds.amountToConfirm', 'Amount to confirm')
                      : formatRefundMoney(refund.total_refund_amount, refund.currency, lang)}
                  </strong>
                  <span className="tkr-row__meta">
                    {[refund.carrier_name, t(`refunds.reasons.${refund.reason}`, refund.reason)].filter(Boolean).join(' · ')}
                  </span>
                </span>
                {refund.created_by_kind === 'ai' && (
                  <span className="u-badge u-badge--accent" title={t('refunds.recordedByAiTitle', 'Recorded by the AI agent from the carrier\'s answer')}>
                    <Sparkles strokeWidth={2} aria-hidden="true" /> {t('refunds.recordedByAi', 'AI')}
                  </span>
                )}
                <Badge tone={REFUND_TONES[refund.status]} dot>{t(`refunds.statuses.${refund.status}`, refund.status)}</Badge>
                <ChevronRight className="tkr-row__chevron" strokeWidth={2} aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <form className="tkr-form" onSubmit={handleSubmit}>
        <h4 className="tkr-form__title">{t('ticketWorkspaceUi.refunds.newTitle', 'Enregistrer un remboursement')}</h4>
        <RefundForm
          form={form}
          onChange={(field, value) => setForm((prev) => ({ ...prev, [field]: value }))}
          declared={context?.declared}
          ticketOrder={orderReference}
          idPrefix="rfm-ticket"
        />

        {error && (
          <div className="u-banner u-banner--danger" role="alert">
            <CircleAlert strokeWidth={2} aria-hidden="true" />
            <div className="u-banner__body">{error}</div>
          </div>
        )}
        {success && (
          <div className="u-banner u-banner--success" role="status">
            <CircleCheck strokeWidth={2} aria-hidden="true" />
            <div className="u-banner__body">{success}</div>
          </div>
        )}

        <div className="tkr-form__actions">
          <button type="submit" className="u-btn u-btn--primary" disabled={saving}>
            <Plus strokeWidth={2} aria-hidden="true" />
            {saving ? t('refunds.saving', 'Saving...') : t('refunds.createRefund', 'Create refund')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default TicketRefunds;
